/**
 * SIP Constants
 * 
 * Shared constants for SIP engine runtime components.
 * Ensures consistent default values across the entire system.
 */

/**
 * Default SIP ports per RFC 3261
 * - UDP/TCP: 5060 (unencrypted)
 * - TLS: 5061 (encrypted)
 * 
 * RFC 3261 defaults for reference only (do not imply runtime listeners).
 */
export const DEFAULT_SIP_PORTS = {
  udp: 5060,
  tcp: 5060,
  tls: 5061
} as const;

/**
 * Default RTP port range for media sessions
 * Standard range: 10000-20000
 */
export const DEFAULT_RTP_PORT_RANGE = {
  start: 10000,
  end: 20000
} as const;

/**
 * SIP transport protocols
 */
export const SIP_TRANSPORTS = ['UDP', 'TCP', 'TLS'] as const;
export type SipTransport = typeof SIP_TRANSPORTS[number];

/**
 * SIP Call Event Types
 * Used for categorizing SIP call events in the database
 */
export const SIP_CALL_EVENT_TYPES = [
  'INVITE',    // Initial call request
  'TRYING',    // 100 Trying provisional
  'RINGING',   // 180 Ringing provisional  
  'ANSWERED',  // 200 OK success
  'BYE',       // Call termination
  'CANCEL',    // Call cancellation
  'ACK',       // Acknowledgment
  'PRACK',     // Provisional acknowledgment
  'ERROR',     // 4xx/5xx error responses
  'REDIRECT'   // 3xx redirect responses
] as const;
export type SipCallEventType = typeof SIP_CALL_EVENT_TYPES[number];

/**
 * Supported DTMF relay modes for SIP media interop.
 */
export const SIP_DTMF_MODES = ['rfc2833', 'sip-info', 'inband'] as const;
export type SipDtmfMode = typeof SIP_DTMF_MODES[number];

/**
 * SBC codec capability set used by runtime negotiation.
 *
 * This is the single source of truth for codecs the platform can advertise
 * and bridge without transcoding.
 */
export const SBC_SUPPORTED_CODECS = [
  'PCMA',
  'PCMU',
  'G722',
  'G729',
  'OPUS',
  'telephone-event',
  'CN',
] as const;

export type SbcSupportedCodec = typeof SBC_SUPPORTED_CODECS[number];

/**
 * Conservative default audio codec preference when no peer codec policy exists.
 */
export const DEFAULT_NEGOTIATION_AUDIO_CODECS = ['PCMU', 'PCMA'] as const;

/**
 * System default DTMF mode when no codec-group policy exists.
 */
export const DEFAULT_DTMF_MODE: SipDtmfMode = 'rfc2833';
